// ═══════════════════════════════════════════════════════════════
// PIPELINE CARD — Individual member card in pipeline column
// Path: src/components/cultivation/PipelineCard.jsx
// ═══════════════════════════════════════════════════════════════

import React from "react";
import { FONT_SIZES, FONT_FAMILY } from "../../constants/appConfig";
import {
  getDaysColor,
  getPipelineAction,
  PIPELINE_URGENCY,
  CONTACT_TYPE_ICONS,
  getLastContactLog,
} from "../../utils/cultivationUtils";

export const PipelineCard = ({
  member,
  engagement,
  stage,
  stageColors,
  palette,
  onClick,
}) => {
  const { daysSinceContact, score } = engagement;
  const daysColor = getDaysColor(daysSinceContact);
  const pipelineAction = getPipelineAction(member, engagement);
  const urgStyle = PIPELINE_URGENCY[pipelineAction.urgency];
  const lastLog = getLastContactLog(member);
  const plannedStep = member.custom_fields?.cultivationNextStep;
  const targetDate = member.custom_fields?.cultivationTargetDate;
  const sc = stageColors;
  const isProspect = member.pathway === "prospect";

  // Persona from enriched data (attached by enrichMembers)
  const persona = member._persona || null;

  // Split action text on \n to render persona hint on its own line
  const actionLines = pipelineAction.text.split("\n");

  return (
    <div
      onClick={() => onClick?.(member)}
      style={{
        background: palette.cardBg,
        borderRadius: 8,
        padding: "12px 14px",
        marginBottom: 8,
        cursor: "pointer",
        borderLeft: `6px solid ${isProspect ? "#ea580c" : daysColor}`,
        transition: "all 0.15s ease",
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateX(3px)";
        e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.1)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateX(0)";
        e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.06)";
      }}
    >
      {/* Row 1: Name + Days + Score */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 4,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontWeight: 700,
              fontSize: 16,
              color: "#111827",
              lineHeight: 1.2,
              fontFamily: FONT_FAMILY,
            }}
          >
            {member.firstName} {member.lastName}
          </div>
          {member.organization && (
            <div
              style={{
                fontSize: 12,
                color: "#6b7280",
                marginTop: 1,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {member.organization}
            </div>
          )}
          {isProspect && (
            <span
              style={{
                display: "inline-block",
                marginTop: 3,
                fontSize: 9,
                fontWeight: 700,
                color: "#c2410c",
                background: "#fff7ed",
                border: "1px solid #fed7aa",
                padding: "1px 6px",
                borderRadius: 3,
                textTransform: "uppercase",
                letterSpacing: 0.3,
              }}
            >
              Prospect
            </span>
          )}
          {member.effective?.mismatch && !isProspect && (
            <span
              style={{
                display: "inline-block",
                marginTop: 3,
                fontSize: 9,
                fontWeight: 700,
                color: "#d97706",
                background: "#fffbeb",
                border: "1px solid #fcd34d",
                padding: "1px 6px",
                borderRadius: 3,
                textTransform: "uppercase",
                letterSpacing: 0.3,
              }}
            >
              ↑ Override
            </span>
          )}
        </div>
        <div style={{ textAlign: "right", marginLeft: 8, flexShrink: 0 }}>
          <div
            style={{
              fontSize: 26,
              fontWeight: 800,
              color: daysColor,
              lineHeight: 1,
            }}
          >
            {daysSinceContact === null ? "—" : daysSinceContact}
          </div>
          <div
            style={{
              fontSize: 10,
              color: "#9ca3af",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: 0.3,
            }}
          >
            {daysSinceContact === null ? "no contact" : "days"}
          </div>
          <div
            style={{
              marginTop: 3,
              fontSize: 11,
              fontWeight: 700,
              color: sc.color,
              background: `${sc.color}18`,
              padding: "2px 8px",
              borderRadius: 8,
              display: "inline-block",
            }}
          >
            {score} pts
          </div>
        </div>
      </div>

      {/* Row 2: Role tags + Persona badge + last contact type */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          marginBottom: 6,
          flexWrap: "wrap",
        }}
      >
        {member.isBoardMember && (
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              padding: "2px 7px",
              borderRadius: 3,
              background: "#dbeafe",
              color: "#1e40af",
            }}
          >
            Board
          </span>
        )}
        {member.isDonor && (
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              padding: "2px 7px",
              borderRadius: 3,
              background: "#dcfce7",
              color: "#166534",
            }}
          >
            Donor
          </span>
        )}
        {!member.assignedDirector?.trim() && (
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              padding: "2px 7px",
              borderRadius: 3,
              background: "#fef3c7",
              color: "#92400e",
            }}
          >
            Unassigned
          </span>
        )}
        {/* ── Persona Badge (Rule 4: solid bg + white text) ── */}
        {persona && (
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              padding: "2px 7px",
              borderRadius: 999,
              background: persona.color || "#6b7280",
              color: "#ffffff",
              whiteSpace: "nowrap",
            }}
          >
            {persona.name}
          </span>
        )}
        {lastLog && (
          <span style={{ marginLeft: "auto", fontSize: 12 }}>
            {CONTACT_TYPE_ICONS[lastLog.type] || "📝"}{" "}
            <span style={{ fontSize: 10, color: "#9ca3af" }}>
              {lastLog.type}
            </span>
          </span>
        )}
      </div>

      {/* Row 3: Next action instruction (with optional persona hint) */}
      <div
        style={{
          background: urgStyle.bg,
          border: `1px solid ${urgStyle.border}`,
          borderRadius: 5,
          padding: "6px 9px",
          fontSize: 12.5,
          lineHeight: 1.35,
          color: urgStyle.text,
          fontWeight: 600,
        }}
      >
        <span
          style={{
            display: "inline-block",
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: urgStyle.dot,
            marginRight: 6,
            verticalAlign: "middle",
          }}
        />
        {/* First line: system action */}
        {actionLines[0]}
        {/* Second line: persona hint (if present) */}
        {actionLines.length > 1 && (
          <div
            style={{
              marginTop: 4,
              paddingTop: 4,
              borderTop: `1px dashed ${urgStyle.border}`,
              fontSize: 11.5,
              fontWeight: 500,
              fontStyle: "italic",
              lineHeight: 1.4,
              color: persona?.color ? persona.color : urgStyle.text,
              opacity: 0.9,
            }}
          >
            {actionLines
              .slice(1)
              .join(" ")
              .replace(/^💡\s*/, "💡 ")}
          </div>
        )}
      </div>

      {/* Row 4 (optional): Director's planned next step */}
      {plannedStep && (
        <div
          style={{
            marginTop: 6,
            padding: "5px 9px",
            background: "#eef2ff",
            borderRadius: 4,
            fontSize: 12,
            color: "#4338ca",
            lineHeight: 1.3,
          }}
        >
          <span style={{ fontWeight: 700 }}>My plan:</span> {plannedStep}
          {targetDate && (
            <span style={{ color: "#6366f1", marginLeft: 4 }}>
              — by {targetDate}
            </span>
          )}
        </div>
      )}
    </div>
  );
};
